import { motion } from 'framer-motion';
import { Monitor, TreePine, Shield, Bus, Utensils, Stethoscope } from 'lucide-react';

const IMAGES = {
  playArea: 'https://customer-assets.emergentagent.com/job_heart-school-kids/artifacts/aosrije0_65a756c4-8e2d-4ad0-b450-c78855e60bc7.jfif',
  hero: 'https://customer-assets.emergentagent.com/job_78008f49-3917-4411-8957-ba588b83fbcc/artifacts/o2yhsu3g_image%2001.jfif',
  fancyDress: 'https://customer-assets.emergentagent.com/job_heart-school-kids/artifacts/0f2rri5e_9237b515-2cc7-4e53-a484-fe933d5892f7.jfif',
};

const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6 }
};

const Facilities = () => {
  const facilities = [
    {
      icon: Monitor,
      title: 'Smart Classrooms',
      description: 'Our classrooms are equipped with interactive learning tools, audio-visual aids, and age-appropriate educational materials that make learning engaging and effective.',
      features: ['Interactive whiteboards', 'Audio-visual equipment', 'Learning corners', 'Child-sized furniture'],
      color: 'bg-gradient-to-br from-cyan-400 to-blue-500',
      bgColor: 'bg-gradient-to-br from-cyan-50 to-blue-50',
    },
    {
      icon: TreePine,
      title: 'Play Area',
      description: 'A colorful, safe outdoor and indoor play area designed to develop motor skills, encourage physical activity, and promote social interaction among children.',
      features: ['Safe play equipment', 'Soft flooring', 'Outdoor games area', 'Sandpit & swings'],
      color: 'bg-gradient-to-br from-green-400 to-emerald-500',
      bgColor: 'bg-gradient-to-br from-green-50 to-emerald-50',
    },
    {
      icon: Shield,
      title: 'Safety & Security',
      description: 'Your child\'s safety is our top priority. We have comprehensive security measures in place to ensure a secure learning environment.',
      features: ['24/7 CCTV monitoring', 'Secure entry gates', 'Trained security staff', 'Emergency protocols'],
      color: 'bg-gradient-to-br from-pink-400 to-rose-500',
      bgColor: 'bg-gradient-to-br from-pink-50 to-rose-50',
    },
    {
      icon: Stethoscope,
      title: 'Hygiene & Health',
      description: 'We maintain the highest standards of cleanliness and hygiene throughout our campus, with regular sanitization and health check-ups.',
      features: ['Daily sanitization', 'Clean restrooms', 'First-aid facility', 'Regular health checks'],
      color: 'bg-gradient-to-br from-purple-400 to-indigo-500',
      bgColor: 'bg-gradient-to-br from-purple-50 to-indigo-50',
    },
    {
      icon: Utensils,
      title: 'Refreshment Area',
      description: 'A clean, hygienic space where children enjoy their snacks and meals in a supervised, comfortable environment.',
      features: ['Hygienic dining area', 'Safe drinking water', 'Supervised meal times', 'Allergy awareness'],
      color: 'bg-gradient-to-br from-orange-400 to-amber-500',
      bgColor: 'bg-gradient-to-br from-orange-50 to-amber-50',
    },
    {
      icon: Bus,
      title: 'Transport',
      description: 'Optional school transport facility with GPS-enabled vehicles and trained attendants for safe commute.',
      features: ['GPS tracking', 'Trained attendants', 'Safe vehicles', 'Doorstep pickup'],
      color: 'bg-gradient-to-br from-teal-400 to-cyan-500',
      bgColor: 'bg-gradient-to-br from-teal-50 to-cyan-50',
      optional: true,
    },
  ];

  return (
    <div className="min-h-screen pt-28 bg-gradient-to-br from-green-50 via-cyan-50 to-blue-50" data-testid="facilities-page">
      {/* Hero Section */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <span className="font-accent text-xl text-teal-500 inline-block -rotate-2 mb-2">
              Our Facilities
            </span>
            <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-bold bg-gradient-to-r from-teal-500 via-cyan-500 to-blue-500 bg-clip-text text-transparent mb-6">
              A Space Designed for Children
            </h1>
            <p className="font-body text-lg md:text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
              Our campus is thoughtfully designed to create a safe, stimulating, and joyful environment where children can learn, play, and grow.
            </p>
          </motion.div>

          {/* Play Area Image */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-[2.5rem] overflow-hidden shadow-xl border-4 border-white"
          >
            <img
              src={IMAGES.playArea}
              alt="Children playing in colorful play area"
              className="w-full h-[300px] md:h-[400px] lg:h-[500px] object-cover"
              data-testid="facilities-hero-image"
            />
          </motion.div>
        </div>
      </section>

      {/* Facilities Grid */}
      <section className="py-16 md:py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {facilities.map((facility, index) => (
              <motion.div
                key={facility.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className={`${facility.bgColor} p-8 rounded-[2rem] transition-all duration-300 hover:-translate-y-2 hover:shadow-xl group relative`}
                data-testid={`facility-${facility.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {facility.optional && (
                  <span className="absolute top-4 right-4 px-3 py-1 bg-white rounded-full text-xs font-body font-semibold text-slate-500">
                    Optional
                  </span>
                )}
                
                <div className={`w-16 h-16 ${facility.color} rounded-2xl flex items-center justify-center mb-6 transition-transform duration-300 group-hover:scale-110 group-hover:rotate-3`}>
                  <facility.icon className="w-8 h-8 text-white" strokeWidth={2.5} />
                </div>
                
                <h3 className="font-heading text-2xl font-semibold text-slate-800 mb-4">
                  {facility.title}
                </h3>
                
                <p className="font-body text-slate-600 leading-relaxed mb-6">
                  {facility.description}
                </p>
                
                <ul className="space-y-2">
                  {facility.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center gap-2">
                      <div className={`w-1.5 h-1.5 ${facility.color} rounded-full`}></div>
                      <span className="font-body text-sm text-slate-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Campus Image */}
      <section className="py-16 md:py-24 bg-gradient-to-b from-white to-sky-50/50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div {...fadeInUp} className="text-center mb-12">
            <span className="font-accent text-xl text-rose-500 inline-block -rotate-2 mb-2">
              Our Campus
            </span>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-semibold text-slate-800">
              A Home Away From Home
            </h2>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="rounded-[2.5rem] overflow-hidden shadow-xl"
          >
            <img
              src={IMAGES.hero}
              alt="Sacred Heart School campus"
              className="w-full h-[300px] md:h-[400px] lg:h-[500px] object-cover"
              data-testid="campus-image"
            />
          </motion.div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-amber-400 to-rose-400">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div {...fadeInUp}>
            <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
              Come Visit Our Campus!
            </h2>
            <p className="font-body text-lg md:text-xl text-white/90 mb-10 max-w-2xl mx-auto">
              We'd love to show you around and let you experience the Sacred Heart difference firsthand.
            </p>
            <a
              href="https://wa.me/919481694328?text=Hello!%20I%20would%20like%20to%20schedule%20a%20campus%20visit%20at%20Sacred%20Heart%20School."
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-white text-rose-500 font-body font-bold rounded-full transition-all duration-300 hover:-translate-y-1 hover:shadow-lg active:translate-y-0 active:scale-95"
              data-testid="schedule-visit-cta"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              Schedule a Visit
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Facilities;
